import { g, Tool } from '@hcie/core';
import { SelectionMode, Point, CanvasLike, EventBus } from '@hcie/shared';
import { 
    fillShapeMask, combineMask, getMaskBounds, extractSelectionBorder,
    getRectBorder, getEllipseBorder 
} from './mask-logic';
import { drawSelectionBorder, startAntsAnimation } from './renderer';
import { 
    SelectionTransformUI, moveSelectionContent, finalizeMoveContent 
} from './transformer';
export { simplifyPath } from './utils';

/**
 * @file index.ts
 * @description Facade and public API for selection tools. Registers EventBus listeners.
 */

// Initialize transform UI
const transformUI = new SelectionTransformUI();
(window as any).transformUI = transformUI;

// Start animation for marching ants
startAntsAnimation();

/**
 * Build selection from rectangle.
 */
export function buildRectSelection(x1: number, y1: number, x2: number, y2: number, mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "rect", x1, y1, x2, y2);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Build selection from ellipse.
 */
export function buildEllipseSelection(x1: number, y1: number, x2: number, y2: number, mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "ellipse", x1, y1, x2, y2);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Build selection from circle.
 */
export function buildCircleSelection(x1: number, y1: number, x2: number, y2: number, mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "circle", x1, y1, x2, y2);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Build selection from a single row.
 */
export function buildSingleRowSelection(y: number, mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "row", 0, y, 0, 0);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Build selection from a single column.
 */
export function buildSingleColumnSelection(x: number, mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "column", x, 0, 0, 0);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Build selection from lasso points.
 */
export function buildLassoSelection(points: Point[], mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0 || !points || points.length < 2) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "lasso", 0, 0, 0, 0, points);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Build selection from polygon points.
 */
export function buildPolygonalSelection(points: Point[], mode: SelectionMode) {
    const w = g.image_width;
    const h = g.image_height;
    if (w <= 0 || h <= 0 || !points || points.length < 2) return;
    const canvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) fillShapeMask(ctx, "poly", 0, 0, 0, 0, points);
    g.selectionCanvas = combineMask(canvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Magic Wand Selection.
 */
export function magicWandSelection(startX: number, startY: number, sourceCtx: CanvasRenderingContext2D, tolerance: number, mode: SelectionMode) {
    const w = sourceCtx.canvas.width;
    const h = sourceCtx.canvas.height;
    const srcData = sourceCtx.getImageData(0, 0, w, h).data;
    const maskData = new Uint8Array(w * h);

    const stack: Point[] = [{ x: startX, y: startY }];
    const startIdx = (startY * w + startX) * 4;
    const sR = srcData[startIdx], sG = srcData[startIdx + 1], sB = srcData[startIdx + 2], sA = srcData[startIdx + 3];

    const visited = new Uint8Array(w * h);
    while (stack.length > 0) {
        const point = stack.pop();
        if (!point) continue;
        const { x, y } = point;
        const idx = y * w + x;
        if (visited[idx]) continue;
        visited[idx] = 1;

        const pIdx = idx * 4;
        if (Math.abs(srcData[pIdx] - sR) <= tolerance && Math.abs(srcData[pIdx+1] - sG) <= tolerance &&
            Math.abs(srcData[pIdx+2] - sB) <= tolerance && Math.abs(srcData[pIdx+3] - sA) <= tolerance) {
            maskData[idx] = 1;
            if (x > 0) stack.push({ x: x - 1, y });
            if (x < w - 1) stack.push({ x: x + 1, y });
            if (y > 0) stack.push({ x, y: y - 1 });
            if (y < h - 1) stack.push({ x, y: y + 1 });
        }
    }

    const mCanvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const mctx = mCanvas.getContext("2d");
    if (mctx) {
        const mImg = mctx.createImageData(w, h);
        for (let i = 0; i < maskData.length; i++) {
            if (maskData[i]) {
                const ii = i * 4;
                mImg.data[ii]=255; mImg.data[ii+1]=255; mImg.data[ii+2]=255; mImg.data[ii+3]=255;
            }
        }
        mctx.putImageData(mImg, 0, 0);
        buildSelectionFromMask(mCanvas, mode);
    }
}

export function buildSelectionFromMask(maskCanvas: HTMLCanvasElement, mode: SelectionMode) {
    if (!maskCanvas) return;
    g.selectionCanvas = combineMask(maskCanvas, mode);
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    g.isSelectionActive = true;
    EventBus.emit('RENDER_LAYERS');
}

export function selectAll() {
    buildRectSelection(0, 0, g.image_width, g.image_height, "replace");
}

export function deselect() {
    if (g.isTransforming && transformUI) {
        transformUI.commit();
    }
    g.isSelectionActive = false;
    g.selectionCanvas = null;
    g.selectionMask = null;
    g.selectionBorder = [];
    (window as any).polyPoints = [];
    (window as any).lassoPoints = [];
    EventBus.emit('RENDER_LAYERS');
}

export function invertSelection() {
    if (!g.isSelectionActive || !g.selectionCanvas) return;
    const w = g.selectionCanvas.width;
    const h = g.selectionCanvas.height;
    const ctx = (g.selectionCanvas as HTMLCanvasElement).getContext("2d");
    if (!ctx) return;
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    for (let i = 3; i < d.length; i += 4) { d[i] = 255 - d[i]; }
    ctx.putImageData(img, 0, 0);
    g.selectionMask = img;
    g.selectionBorder = extractSelectionBorder(g.selectionCanvas as HTMLCanvasElement);
    EventBus.emit('RENDER_LAYERS');
}

export function moveSelectionMask(dx: number, dy: number) {
    if (!g.isSelectionActive || !g.selectionCanvas) return;
    const w = g.selectionCanvas.width;
    const h = g.selectionCanvas.height;
    const newCanvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const nctx = newCanvas.getContext("2d");
    if (nctx) {
        nctx.drawImage(g.selectionCanvas as HTMLCanvasElement, dx, dy);
        g.selectionCanvas = newCanvas;
        g.selectionMask = nctx.getImageData(0, 0, w, h);
    }
    if (g.selectionBorder) {
        g.selectionBorder = g.selectionBorder.map((path: Point[]) =>
            path.map((p: Point) => ({ x: p.x + dx, y: p.y + dy }))
        );
    }
    EventBus.emit('RENDER_LAYERS');
}

export function transformSelectionMask(params: { x: number, y: number, angle: number, scaleX: number, scaleY: number, originalBounds: any }) {
    if (!g.isSelectionActive) return;

    // Use backed up original mask to prevent cumulative drift/blur
    let baseCanvas = (g as any)._originalSelectionCanvas;
    let baseBorder = (g as any)._originalSelectionBorder;

    if (!baseCanvas && g.selectionCanvas) {
        baseCanvas = g.createCanvas(g.selectionCanvas.width, g.selectionCanvas.height);
        const bctx = baseCanvas.getContext('2d');
        if (bctx) bctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
        (g as any)._originalSelectionCanvas = baseCanvas;
        (g as any)._originalSelectionBorder = JSON.parse(JSON.stringify(g.selectionBorder || []));
        baseBorder = (g as any)._originalSelectionBorder;
    }

    if (!baseCanvas || !params.originalBounds) return;

    const w = baseCanvas.width;
    const h = baseCanvas.height;
    const newCanvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const ctx = newCanvas.getContext('2d');
    
    if (ctx) {
        const ob = params.originalBounds;
        const centerX = ob.x + ob.w / 2;
        const centerY = ob.y + ob.h / 2;
        const dx = params.x - ob.x;
        const dy = params.y - ob.y;

        ctx.save();
        ctx.translate(centerX + dx, centerY + dy);
        if (params.angle) ctx.rotate(params.angle);
        ctx.scale(params.scaleX, params.scaleY);
        ctx.drawImage(baseCanvas, -ob.w / 2 - ob.x, -ob.h / 2 - ob.y);
        ctx.restore();

        g.selectionCanvas = newCanvas;
    }

    // Transform border points
    if (baseBorder) {
        const ob = params.originalBounds;
        const centerX = ob.x + ob.w / 2;
        const centerY = ob.y + ob.h / 2;
        const dx = params.x - ob.x;
        const dy = params.y - ob.y;
        const cos = Math.cos(params.angle);
        const sin = Math.sin(params.angle);

        g.selectionBorder = baseBorder.map((path: Point[]) => path.map(p => {
            // 1. Translate to origin relative to rotation center
            let tx = p.x - centerX;
            let ty = p.y - centerY;
            
            // 2. Rotate
            let rx = tx * cos - ty * sin;
            let ry = tx * sin + ty * cos;
            
            // 3. Scale
            rx *= params.scaleX;
            ry *= params.scaleY;
            
            // 4. Translate back with movement delta
            return {
                x: rx + centerX + dx,
                y: ry + centerY + dy
            };
        }));
    }

    EventBus.emit('RENDER_LAYERS');
}

export function isPointInSelection(x: number, y: number) {
    if (!g.isSelectionActive || !g.selectionCanvas) return false;
    const w = g.selectionCanvas.width;
    const h = g.selectionCanvas.height;
    const ix = Math.floor(x), iy = Math.floor(y);
    if (ix < 0 || ix >= w || iy < 0 || iy >= h) return false;
    const ctx = (g.selectionCanvas as HTMLCanvasElement).getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    const img = ctx.getImageData(ix, iy, 1, 1);
    return img.data[3] > 10;
}

// EventBus Listeners
EventBus.on('TOOL_CHANGED', (payload) => {
    if (g.isTransforming && transformUI && payload.toolId !== Tool.MoveContent.id) {
        transformUI.commit();
    }
    
    // Auto-activate transformation UI when switching to MoveContent tool
    if (payload.toolId === Tool.MoveContent.id && g.isSelectionActive && !g.floatingContent) {
        moveSelectionContent(0, 0); // Trigger extraction and UI show
    }
});

EventBus.on('MOVE_SELECTION_MASK', (payload) => {
    moveSelectionMask(payload.dx, payload.dy);
});

EventBus.on('TRANSFORM_SELECTION_MASK', (payload) => {
    transformSelectionMask(payload);
});

EventBus.on('FINISH_DRAWING', () => {
    (g as any)._originalSelectionCanvas = null;
    (g as any)._originalSelectionBorder = null;
});

// Legacy window bindings for components not yet converted
if (typeof window !== 'undefined') {
    Object.assign(window, {
        buildRectSelection, buildEllipseSelection, buildLassoSelection,
        buildPolygonalSelection, buildSelectionFromMask, drawSelectionBorder,
        getRectBorder, getEllipseBorder,
        selectAll, deselect, invertSelection, isPointInSelection, getMaskBounds,
        moveSelectionMask, moveSelectionContent, finalizeMoveContent, magicWandSelection,
        transformSelectionMask
    });
}

export * from './mask-logic';
export * from './renderer';
export * from './transformer';
